import { useSyncExternalStore } from 'react'
import { type z } from 'zod'
import { logger } from 'services/logger'
import StorageManager from 'services/StorageManager'

export type LocalStorageKey<Value> = {
  getValue: () => Value | null
  setValue: (value: Value) => void
  removeValue: () => void
  subscribe: (onValueChange: () => void) => () => void
}

function parseStoredValue<Value>(
  key: string,
  schema: z.ZodType<Value, z.ZodTypeDef, unknown>
): Value | null {
  // An absent key is "nothing stored yet", not an invalid value, so it isn't logged.
  if (!StorageManager.hasKey(key)) return null
  const parseResult = schema.safeParse(StorageManager.get(key))
  if (parseResult.success) return parseResult.data
  logger.error('Invalid stored value', parseResult.error, { key })
  return null
}

// Loaded once, kept in memory, written through: the same model as the persisted MobX stores.
export function createLocalStorageKey<Value>(
  key: string,
  schema: z.ZodType<Value, z.ZodTypeDef, unknown>
): LocalStorageKey<Value> {
  const listeners = new Set<() => void>()
  let value: Value | null | undefined // undefined until the first read
  const notifyListeners = () =>
    listeners.forEach((onValueChange) => onValueChange())
  return {
    getValue: () => {
      if (value === undefined) value = parseStoredValue(key, schema)
      return value
    },
    setValue: (nextValue) => {
      value = nextValue
      StorageManager.set(key, nextValue)
      notifyListeners()
    },
    removeValue: () => {
      value = null
      StorageManager.remove(key)
      notifyListeners()
    },
    subscribe: (onValueChange) => {
      listeners.add(onValueChange)
      return () => listeners.delete(onValueChange)
    },
  }
}

export function useLocalStorageKey<Value>(
  storageKey: LocalStorageKey<Value>
): Value | null {
  return useSyncExternalStore(
    storageKey.subscribe,
    storageKey.getValue,
    () => null
  )
}
